import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SiteTheme, SiteThemeService } from '../../services/site-theme.service';

@Component({
  selector: 'app-site-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './site-header.component.html',
  styleUrl: './site-header.component.css'
})
export class SiteHeaderComponent {
  private readonly themeService = inject(SiteThemeService);
  private readonly document = inject(DOCUMENT);

  @ViewChild('menuToggleButton') private menuToggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('mobileDrawer') private mobileDrawer?: ElementRef<HTMLElement>;

  readonly mobileMenuId = 'mobile-primary-navigation';
  readonly mobileThemeMenuId = 'mobile-theme-options';

  readonly navItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Q & A', path: '/q-and-a' },
    { label: 'Testimonials', path: '/testimonials' },
    { label: 'Contact Us', path: '/contact-us' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Legal', path: '/legal' }
  ];

  readonly themeOptions: Array<{ label: string; value: SiteTheme }> = [
    { label: 'Black & White', value: 'black-white' },
    { label: 'Charcoal', value: 'charcoal' },
    { label: 'Deep Blue', value: 'deep-blue' },
    { label: 'Deep Purple', value: 'deep-purple' },
    { label: 'Forest Green', value: 'forest-green' },
    { label: 'Terracotta', value: 'terracotta' },
    { label: 'Yelo', value: 'yelo' }
  ];

  readonly sortedThemeOptions = [...this.themeOptions].sort((a, b) => a.label.localeCompare(b.label));

  readonly currentTheme = this.themeService.currentTheme;
  readonly isThemeMenuOpen = signal(false);
  readonly isMenuOpen = signal(false);
  readonly isMobileThemeMenuOpen = signal(false);

  readonly themeLabelByValue: Record<SiteTheme, string> = {
    'black-white': 'Black & White',
    'charcoal': 'Charcoal',
    'deep-blue': 'Deep Blue',
    'deep-purple': 'Deep Purple',
    'forest-green': 'Forest Green',
    'terracotta': 'Terracotta',
    'yelo': 'Yelo'
  };

  setTheme(theme: SiteTheme): void {
    this.themeService.setTheme(theme);
  }

  selectTheme(theme: SiteTheme): void {
    this.setTheme(theme);
    this.isThemeMenuOpen.set(false);
  }

  toggleThemeMenu(): void {
    this.isThemeMenuOpen.update(isOpen => !isOpen);
  }

  openMenu(): void {
    this.isMenuOpen.set(true);
    this.setBodyScrollLock(true);
    queueMicrotask(() => {
      this.focusFirstMenuItem();
    });
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
    this.isMobileThemeMenuOpen.set(false);
    this.setBodyScrollLock(false);
    queueMicrotask(() => {
      const toggleButton = this.menuToggleButton?.nativeElement;
      if (toggleButton) {
        toggleButton.focus();
      }
    });
  }

  toggleMenu(): void {
    if (this.isMenuOpen()) {
      this.closeMenu();
      return;
    }

    this.openMenu();
  }

  onNavItemSelected(): void {
    this.closeMenu();
    this.isThemeMenuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeMenu();
    this.isThemeMenuOpen.set(false);
  }

  toggleMobileThemeMenu(): void {
    this.isMobileThemeMenuOpen.update(isOpen => !isOpen);
  }

  openMobileThemeMenu(): void {
    this.isMobileThemeMenuOpen.set(true);
  }

  closeMobileThemeMenu(): void {
    this.isMobileThemeMenuOpen.set(false);
  }

  selectThemeFromMobile(theme: SiteTheme): void {
    this.selectTheme(theme);
    this.closeMenu();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (!this.isMenuOpen() || event.key !== 'Tab') {
      return;
    }

    const drawer = this.mobileDrawer?.nativeElement;
    if (!drawer) {
      return;
    }

    const focusableElements = drawer.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) {
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const active = this.document.activeElement as HTMLElement | null;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusFirstMenuItem(): void {
    const firstMenuItem = this.mobileDrawer?.nativeElement.querySelector<HTMLElement>(
      'a[href], button:not([disabled])'
    );
    if (firstMenuItem) {
      firstMenuItem.focus();
    }
  }

  private setBodyScrollLock(locked: boolean): void {
    this.document.body.style.overflow = locked ? 'hidden' : '';
  }

  getCurrentThemeLabel(): string {
    return this.themeLabelByValue[this.currentTheme()];
  }
}
